import { ScanSearch } from "lucide-react";

/**
 * Screen Check badge — surfaces the V3 Instant Quote "check the rest
 * of your screens" answer wherever a tech will see the job.
 *
 * - whole_home_check → green (every screen on-site)
 * - quick_check      → amber (spot-check obvious issues)
 * - selected_only / null → renders nothing (no signal worth surfacing)
 *
 * Sizes:
 *   xs = tiny pill for week-view job cards
 *   sm = pill + short label for day-view / list cards
 *   md = full banner with description for JobDialog / detail views
 */
export function ScreenCheckBadge({ preference, size = "sm" }) {
  if (!preference || preference === "selected_only") return null;

  const isWhole = preference === "whole_home_check";
  const label = isWhole ? "Whole-Home Check" : "Quick Check";
  const short = isWhole ? "Whole-Home" : "Quick Check";
  const description = isWhole
    ? "Please check every window and door screen while you're there."
    : "Note any other screens that could use attention — no pressure.";

  const colors = isWhole
    ? "border-[#207024] bg-green-50 text-[#207024]"
    : "border-amber-400 bg-amber-50 text-amber-700";
  const iconBg = isWhole ? "bg-[#207024]" : "bg-amber-500";

  if (size === "xs") {
    return (
      <span
        data-testid={`screen-check-badge-xs-${preference}`}
        className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold border ${colors}`}
        title={label}
      >
        <ScanSearch className="w-2.5 h-2.5" />
        {short}
      </span>
    );
  }

  if (size === "sm") {
    return (
      <span
        data-testid={`screen-check-badge-sm-${preference}`}
        className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold border ${colors}`}
        title={description}
      >
        <ScanSearch className="w-3 h-3" />
        {label}
      </span>
    );
  }

  // md — full banner
  return (
    <div
      data-testid={`screen-check-badge-md-${preference}`}
      className={`rounded-lg border-2 p-3 flex items-start gap-3 ${colors}`}
    >
      <div className={`mt-0.5 h-6 w-6 rounded-full ${iconBg} text-white flex items-center justify-center flex-shrink-0`}>
        <ScanSearch className="w-3.5 h-3.5" />
      </div>
      <div className="text-sm">
        <div className="font-bold uppercase tracking-wider text-xs">Screen Check</div>
        <div className="font-semibold text-zinc-900">
          Customer requested a {label}
        </div>
        <div className="text-zinc-600 text-xs mt-0.5">{description}</div>
      </div>
    </div>
  );
}
